import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Banknote, Loader2, Users, Footprints } from 'lucide-react';
import WalkInDialog from './WalkInDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const monthStart = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};
const todayISO = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kuwait', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

const WalkInEarnings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = localStorage.getItem('sessionToken');
  const [range, setRange] = useState({ from: monthStart(), to: todayISO() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [repeatBooking, setRepeatBooking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/provider/walk-ins`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date_from: range.from, date_to: range.to },
      });
      setData(res.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || t('walkIn.loadFailed', 'Could not load walk-in earnings'));
    } finally {
      setLoading(false);
    }
  }, [token, range, t]);

  useEffect(() => { load(); }, [load]);

  const repeat = async (row) => {
    try {
      const res = await axios.get(`${API}/provider/walk-in/${row.id}/repeat`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: todayISO() },
      });
      setRepeatBooking({
        prefill: res.data,
        slot: { staffId: res.data.staff_id, staffName: row.staff_name, time: res.data.suggested_time || row.time },
      });
    } catch {
      setError(t('walkIn.repeatFailed', 'Could not load that customer'));
    }
  };

  const dateInput = 'rounded-lg border border-slate-300 px-3 py-2 text-sm numeric-ltr';

  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="walk-in-earnings-page">
      <div className="mx-auto max-w-5xl px-4 pt-6">
        <button
          onClick={() => navigate('/provider-dashboard?tab=revenue')}
          data-testid="walk-in-back-btn"
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t('walkIn.backToRevenue', 'Back to revenue')}
        </button>

        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-amber-600">{t('walkIn.badge', 'Walk-in')}</p>
              <h1 className="text-2xl font-bold text-slate-900">{t('walkIn.earningsTitle', 'Walk-in earnings')}</h1>
              <p className="text-sm text-slate-500">
                {t('walkIn.earningsSubtitle', 'Logged in person at the shop. EnayaHub takes no commission on these.')}
              </p>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <label className="mb-1 block text-[11px] font-bold text-slate-500">{t('walkIn.from', 'From')}</label>
                <input type="date" className={dateInput} value={range.from} data-testid="walk-in-from"
                  onChange={(e) => setRange({ ...range, from: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-bold text-slate-500">{t('walkIn.to', 'To')}</label>
                <input type="date" className={dateInput} value={range.to} data-testid="walk-in-to"
                  onChange={(e) => setRange({ ...range, to: e.target.value })} />
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="mt-6 flex items-center justify-center gap-2 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" /> {t('walkIn.loading', 'Loading walk-ins…')}
          </div>
        )}
        {error && <p className="mt-6 rounded-xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700" data-testid="walk-in-page-error">{error}</p>}

        {data && !loading && (
          <>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { icon: Footprints, label: t('walkIn.totalWalkIns', 'Walk-ins'), value: data.summary.total_walk_ins, testid: 'walk-in-count' },
                { icon: Banknote, label: t('walkIn.totalEarnings', 'Your earnings'), value: `${data.summary.total_earnings} ${t('common.currency', 'KWD')}`, testid: 'walk-in-earnings' },
                { icon: Banknote, label: t('walkIn.commission', 'Commission charged'), value: `0 ${t('common.currency', 'KWD')}`, testid: 'walk-in-commission' },
                { icon: Users, label: t('walkIn.averageTicket', 'Average ticket'), value: `${data.summary.average_ticket} ${t('common.currency', 'KWD')}`, testid: 'walk-in-average' },
              ].map((card) => (
                <div key={card.label} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={card.testid}>
                  <card.icon className="h-4 w-4 text-slate-400" />
                  <p className="numeric-ltr mt-2 text-xl font-bold text-slate-900">{card.value}</p>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{card.label}</p>
                </div>
              ))}
            </div>

            {data.by_staff.length > 0 && (
              <div className="mt-5 rounded-2xl bg-white p-5 shadow-sm">
                <h2 className="mb-3 text-sm font-bold text-slate-900">{t('walkIn.byStaff', 'Credited to staff')}</h2>
                <ul className="space-y-2">
                  {data.by_staff.map((s) => (
                    <li key={s.staff_id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm"
                      data-testid={`walk-in-staff-${s.staff_id}`}>
                      <span className="font-semibold text-slate-700">{s.staff_name}</span>
                      <span className="numeric-ltr text-slate-600">{s.walk_ins} · {s.earnings} {t('common.currency', 'KWD')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-5 overflow-hidden rounded-2xl bg-white shadow-sm">
              <h2 className="border-b border-slate-100 px-5 py-4 text-sm font-bold text-slate-900">
                {t('walkIn.list', 'Walk-in bookings')}
              </h2>
              {data.walk_ins.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-slate-500" data-testid="walk-in-empty">
                  {t('walkIn.empty', 'No walk-ins in this period yet — tap an empty slot on the day board to add one.')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-start text-[11px] font-bold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-2 text-start">{t('walkIn.date', 'Date')}</th>
                        <th className="px-4 py-2 text-start">{t('walkIn.customer', 'Customer')}</th>
                        <th className="px-4 py-2 text-start">{t('walkIn.service', 'Service')}</th>
                        <th className="px-4 py-2 text-start">{t('walkIn.staff', 'Staff member')}</th>
                        <th className="px-4 py-2 text-end">{t('walkIn.amount', 'Amount')}</th>
                        <th className="px-4 py-2 text-end"> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.walk_ins.map((w) => (
                        <tr key={w.id} className="border-t border-slate-100" data-testid={`walk-in-row-${w.id}`}>
                          <td className="numeric-ltr px-4 py-2 text-slate-600">{w.date} {w.time}</td>
                          <td className="px-4 py-2 font-semibold text-slate-800">{w.customer_name}</td>
                          <td className="px-4 py-2 text-slate-600">{w.service_name}</td>
                          <td className="px-4 py-2 text-slate-600">{w.staff_name}</td>
                          <td className="numeric-ltr px-4 py-2 text-end font-bold text-slate-900">{w.amount} {t('common.currency', 'KWD')}</td>
                          <td className="px-4 py-2 text-end">
                            <button
                              onClick={() => repeat(w)}
                              data-testid={`walk-in-repeat-${w.id}`}
                              className="rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-white hover:bg-amber-600"
                            >
                              {t('walkIn.bookAgain', 'Book again')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {repeatBooking && (
        <WalkInDialog
          slot={repeatBooking.slot}
          date={todayISO()}
          prefill={repeatBooking.prefill}
          onClose={() => setRepeatBooking(null)}
          onCreated={() => { setRepeatBooking(null); load(); }}
        />
      )}
    </div>
  );
};

export default WalkInEarnings;
